<script lang="ts" setup>
import { SshKey as SshKeySchema, type SshKey, type SshKeyCreateRequest } from '@/types'
import { STATUS_CODES } from '@/constants'
import { reactive, watch } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { useI18n } from 'vue-i18n'
import { isValidSshKey } from '@/utils/sshKey'

const props = defineProps<{
  show: boolean
}>()

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'created', key: SshKey): void
}>()

const notifyStore = useNotifyStore()
const { t } = useI18n()

const data = reactive({
  showDialog: false,
  loading: false,
  name: '',
  publicKey: ''
})

const handleClose = () => {
  data.showDialog = false
}

const resetForm = () => {
  data.name = ''
  data.publicKey = ''
}

const handleCreate = async () => {
  const name = data.name.trim()
  const publicKey = data.publicKey.trim()
  if (!name || !publicKey) return

  if (!isValidSshKey(publicKey)) {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'warning',
      title: t('dashboard.account.sshKeys.create.invalidKey'),
      message: ''
    })
    return
  }

  data.loading = true

  const requestData: SshKeyCreateRequest = {
    name,
    public_key: publicKey
  }

  try {
    const res = await useAxios<SshKeyCreateRequest, SshKey>(
      'POST',
      '/account/ssh-keys',
      true,
      requestData
    )

    if (res && res.status === STATUS_CODES.CREATED && res.data) {
      const validate = SshKeySchema.safeParse(res.data)
      if (validate.success) {
        emits('created', validate.data)
        handleClose()
      }
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'SshKeyCreateModal' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error as Error)
    })
  } finally {
    data.loading = false
  }
}

watch(
  () => props.show,
  (newVal) => {
    data.showDialog = newVal
    if (newVal) {
      resetForm()
    }
  },
  { immediate: true }
)

watch(
  () => data.showDialog,
  (newVal) => {
    if (!newVal) {
      emits('close')
    }
  }
)
</script>

<template>
  <v-dialog v-model="data.showDialog" max-width="560" persistent>
    <div class="create-modal">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-icon">
          <v-icon icon="mdi-key-plus" size="24" />
        </div>
        <div class="header-text">
          <h2 class="modal-title">{{ $t('dashboard.account.sshKeys.create.title') }}</h2>
        </div>
        <button class="close-btn" @click="handleClose">
          <v-icon icon="mdi-close" size="22" />
        </button>
      </div>

      <!-- Form -->
      <div class="modal-content">
        <div class="form-group">
          <label class="form-label">{{ $t('dashboard.account.sshKeys.create.name') }}</label>
          <input
            v-model="data.name"
            type="text"
            class="form-input"
            :placeholder="$t('dashboard.account.sshKeys.create.namePlaceholder')"
          />
          <span class="form-hint">
            <v-icon icon="mdi-information-outline" size="14" />
            {{ $t('dashboard.account.sshKeys.create.nameHint') }}
          </span>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('dashboard.account.sshKeys.create.publicKey') }}</label>
          <textarea
            v-model="data.publicKey"
            class="form-input form-textarea"
            rows="4"
            :placeholder="$t('dashboard.account.sshKeys.create.publicKeyPlaceholder')"
          />
          <span class="form-hint">
            <v-icon icon="mdi-information-outline" size="14" />
            {{ $t('dashboard.account.sshKeys.create.publicKeyHint') }}
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn secondary" :disabled="data.loading" @click="handleClose">
          {{ $t('common.cancel') }}
        </button>
        <button
          class="btn primary"
          :disabled="data.loading || !data.name.trim() || !data.publicKey.trim()"
          @click="handleCreate"
        >
          <v-progress-circular v-if="data.loading" indeterminate size="18" width="2" />
          <v-icon v-else icon="mdi-plus" size="18" />
          {{ $t('dashboard.account.sshKeys.createKey') }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.create-modal {
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(33, 150, 243, 0.12);
  border: 1px solid rgba(33, 150, 243, 0.25);
  border-radius: 12px;
  color: #64b5f6;
}

.header-text {
  flex: 1;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

.modal-content {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.8);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(33, 150, 243, 0.4);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 96px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  word-break: break-all;
}

.form-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);

  .v-icon {
    color: rgba(200, 218, 255, 0.4);
  }
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.secondary {
    background: rgba(120, 163, 255, 0.08);
    border: 1px solid rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.8);

    &:hover:not(:disabled) {
      background: rgba(120, 163, 255, 0.15);
    }
  }

  &.primary {
    background: rgba(33, 150, 243, 0.15);
    border: 1px solid rgba(33, 150, 243, 0.3);
    color: #64b5f6;

    &:hover:not(:disabled) {
      background: rgba(33, 150, 243, 0.25);
      border-color: rgba(33, 150, 243, 0.45);
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .create-modal {
    background: rgba(255, 255, 255, 0.98);
  }

  .modal-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .form-label {
    color: rgba(15, 23, 42, 0.8);
  }

  .form-input {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.9);

    &::placeholder {
      color: rgba(15, 23, 42, 0.4);
    }
  }

  .form-hint {
    color: rgba(15, 23, 42, 0.5);
  }
}
</style>
